import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { ClipboardCheck, RefreshCw, Search, UserCheck, Users, X } from 'lucide-react';
import { formatDT } from '../../utils/timezone';
import { PaginationFooter, usePersistedPageSize } from '../shared/PaginationFooter';
import { DateRangePresetFilter } from '../shared/DateRangePresetFilter';
import DriverConfirmDialog from '../events/DriverConfirmDialog';
import BulkDriverAssignDialog from './BulkDriverAssignDialog';

const stateBadge = (c) => {
  if (c.manager_review_required) {
    return { label: 'Review required', cls: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700' };
  }
  return { label: 'Awaiting responses', cls: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700' };
};

const waitingSince = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export const PendingReviewsPanel = ({ api, onCountChange, onResolved }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('pending-reviews', 10);
  const [loading, setLoading] = useState(true);
  const [reviewCandidateId, setReviewCandidateId] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [dateResetKey, setDateResetKey] = useState(0);

  useEffect(() => {
    const handle = setTimeout(() => setSearchDebounced(searchQuery.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Back to page 1 whenever a filter changes so we always land on real results; drop the
  // selection too so bulk assign never acts on rows hidden by the new filters.
  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced, dateRange.from, dateRange.to]);

  const filterParams = useCallback((params) => {
    if (searchDebounced) params.set('search', searchDebounced);
    if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
    if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
    return params;
  }, [searchDebounced, dateRange.from, dateRange.to]);

  const filtersActive = !!(searchDebounced || dateRange.from || dateRange.to);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterParams(new URLSearchParams({ status: 'pending', skip: String(page * pageSize), limit: String(pageSize) }));
      const res = await api.get(`/fleet/driver-candidates?${params.toString()}`);
      const list = res.data?.candidates || [];
      const totalCount = res.data?.total ?? list.length;
      setRows(list);
      setTotal(totalCount);
      // The People-tab badge is the GLOBAL pending count — only report it unfiltered.
      if (!filtersActive) onCountChange?.(totalCount);
      if (list.length === 0 && page > 0 && totalCount > 0) {
        setPage(Math.max(0, Math.ceil(totalCount / pageSize) - 1));
      }
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, onCountChange, page, pageSize, filterParams, filtersActive]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const selectAllAcrossPages = async () => {
    if (selectedIds.length >= total) {
      setSelectedIds([]);
      return;
    }
    try {
      setSelectAllLoading(true);
      const limit = Math.min(Math.max(total, 1), 500);
      const params = filterParams(new URLSearchParams({ status: 'pending', skip: '0', limit: String(limit) }));
      const res = await api.get(`/fleet/driver-candidates?${params.toString()}`);
      const allIds = (res.data?.candidates || []).map((c) => c.id).filter(Boolean);
      setSelectedIds(allIds);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to select all pending reviews');
    } finally {
      setSelectAllLoading(false);
    }
  };

  const pageIds = rows.map((c) => c.id).filter(Boolean);

  return (
    <Card data-testid="pending-reviews-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-foreground" />
            <h3 className="text-base font-semibold" data-testid="pending-reviews-title">Pending Reviews</h3>
            {total > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-1.5 py-0" data-testid="pending-reviews-count">{total}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <Button
                size="sm"
                className="h-8 bg-[#2563eb] hover:bg-[#1d4ed8]"
                onClick={() => setBulkOpen(true)}
                data-testid="pending-reviews-bulk-assign-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Bulk Assign Driver ({selectedIds.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={fetchQueue} disabled={loading} data-testid="pending-reviews-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Trips still waiting for a driver decision — assign a candidate, pick from the directory, or confirm No Driver. Tick rows to assign the same driver to several trips at once.</p>
        {/* Vehicle search + Raised date range — same UX as Admin > Commands > Command History */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search vehicle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-10 pr-8 dark:bg-[#0D1425]"
              data-testid="pending-reviews-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="pending-reviews-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <DateRangePresetFilter
            value={dateRange}
            onChange={setDateRange}
            testIdPrefix="pending-reviews-date"
            resetSignal={dateResetKey}
            triggerClassName="dark:bg-[#0D1425]"
          />
          {filtersActive && (
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
              onClick={() => { setSearchQuery(''); setDateRange({ from: undefined, to: undefined }); setDateResetKey((k) => k + 1); }}
              data-testid="pending-reviews-filters-clear">
              Clear filters
            </Button>
          )}
        </div>
        {rows.length > 0 && (
          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="pending-reviews-selection-bar">
            <span data-testid="pending-reviews-selection-count">{selectedIds.length} selected</span>
            {selectedIds.length > 0 && (<>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                data-testid="pending-reviews-select-all-toggle"
                disabled={selectAllLoading}
                onClick={selectAllAcrossPages}
              >
                {selectAllLoading ? 'Selecting…' : (selectedIds.length >= total ? 'Deselect All' : 'Select All')}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedIds([])} data-testid="pending-reviews-selection-clear">Clear</Button>
            </>)}
          </div>
        )}
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="pending-reviews-empty">
            {loading ? 'Loading…' : (filtersActive ? 'No pending reviews match the current filters.' : 'No trips waiting for review.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="pending-reviews-select-all"
                      checked={pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))}
                      onCheckedChange={(checked) => {
                        setSelectedIds((prev) => checked
                          ? Array.from(new Set([...prev, ...pageIds]))
                          : prev.filter((id) => !pageIds.includes(id)));
                      }}
                    />
                  </TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Raised</TableHead>
                  <TableHead>Candidates</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c, idx) => {
                  const badge = stateBadge(c);
                  const raised = c.ballot_sent_at || c.created_at;
                  return (
                    <TableRow key={c.id} className="text-xs" data-testid={`pending-review-row-${idx}`}>
                      <TableCell>
                        <Checkbox
                          data-testid={`pending-review-select-${idx}`}
                          checked={!!c.id && selectedIds.includes(c.id)}
                          disabled={!c.id}
                          onCheckedChange={(checked) => {
                            if (!c.id) return;
                            setSelectedIds((prev) => checked
                              ? Array.from(new Set([...prev, c.id]))
                              : prev.filter((id) => id !== c.id));
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{c.vehicle_name || c.vehicle_device_id}</TableCell>
                      <TableCell>
                        <span>{raised ? formatDT(raised, { seconds: true }) : '—'}</span>
                        {raised && <span className="text-muted-foreground ml-1">({waitingSince(raised)})</span>}
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate">
                        {(c.candidates || []).map((p) => p.person_name).filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${badge.cls}`}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="h-7 bg-[#2563eb] hover:bg-[#1d4ed8]"
                          onClick={() => { setReviewCandidateId(c.id); setReviewOpen(true); }}
                          data-testid={`pending-review-open-${idx}`}
                        >
                          <UserCheck className="w-3.5 h-3.5 mr-1" /> Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {total > 0 && (
          <PaginationFooter
            page={page}
            setPage={setPage}
            totalItems={total}
            itemsPerPage={pageSize}
            setItemsPerPage={setPageSize}
            testIdPrefix="pending-reviews"
            zeroIndexed
            disabled={loading}
            alwaysShow
          />
        )}
        <DriverConfirmDialog
          api={api}
          candidateId={reviewCandidateId}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          onResolved={() => { fetchQueue(); onResolved?.(); }}
        />
        <BulkDriverAssignDialog
          api={api}
          candidateIds={selectedIds}
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          onResolved={() => { setSelectedIds([]); fetchQueue(); onResolved?.(); }}
        />
      </CardContent>
    </Card>
  );
};
